import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { LoginUser, LoginWithOtp } from "./loginUser.interface";
import authService from "./services/auth.service";
import { addAfterLoginNotification } from "../../../components/Toast/utils/notifications";

interface AsyncState {
  isLoading: {
    profileData: boolean;
    forgotPasswordData: boolean;
    resetPasswordData: boolean;
    logoutData: boolean;
    userData: boolean;
    otpVerificationData: boolean;
    signUpData: boolean;
  };
  isSuccess: boolean;
  isError: boolean;
  isForgotPasswordSuccess: boolean;
  isresetUserPasswordSuccess: boolean;
}

interface AuthState extends AsyncState {
  userData?: any | null;
  otpData?: any | null;
  profileData?: any | null;
  signUpResponse?: any | null;
  m_user_id?: any;
  resetPasswordData?: any | null;
  forgotPasswordData?: any | null;
  logoutData?: any | null;
  clientAuthData?: any | null;
  errorCode?: any | null;
  errorMsg?: any | null;
  clientToken?: any | null;
  notification?: any;
  notification_type?: any;
  expires_in?: any;
}

const initialState: AuthState = {
  isLoading: {
    profileData: true,
    forgotPasswordData: true,
    resetPasswordData: true,
    logoutData: true,
    userData: false,
    otpVerificationData: false,
    signUpData: false,
  },
  isSuccess: false,
  isError: false,
  isForgotPasswordSuccess: false,
  userData: null,
  otpData: null,
  signUpResponse: null,
  profileData: null,
  isresetUserPasswordSuccess: false,
  resetPasswordData: null,
  forgotPasswordData: null,
  logoutData: null,
  clientAuthData: null,
  errorCode: null,
  errorMsg: null,
  clientToken: localStorage.getItem("clientToken"),
  notification: null,
};

export const login = createAsyncThunk(
  "auth/login",
  async (user: LoginUser, thunkAPI) => {
    try {
      return await authService.login(user);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loginWithOtp = createAsyncThunk(
  "auth/login-with-otp",
  async (otpDetails: LoginWithOtp, thunkAPI) => {
    try {
      return await authService.loginWithOtp(otpDetails);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgot-password/?email",
  async (forgotPasswordDetails: any, thunkAPI) => {
    try {
      return await authService.forgotPassword(forgotPasswordDetails);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const resetUserPassword = createAsyncThunk(
  "auth/reset-password/?token",
  async (resetUser: any, thunkAPI) => {
    try {
      return await authService.resetUserPassword(resetUser);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const logout = createAsyncThunk(
  "/auth/logout",
  async (user: any, thunkAPI) => {
    try {
      return await authService.logout(user);
    } catch (error) {
      thunkAPI.dispatch(resetLogout(error));
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const userProfile = createAsyncThunk(
  "/profile",
  async (user: any | null, thunkAPI) => {
    try {
      return await authService.getProfileData();
    } catch (error) {
      thunkAPI.dispatch(resetLogout(error));
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const signUpNewUser = createAsyncThunk(
  "/auth/signup",
  async (data: any | null, thunkAPI) => {
    try {
      const res = await authService.signUp(data);
      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetSignUp: (state) => {
      state.signUpResponse = {};
    },
    reset: (state) => {
      state.isSuccess = false;
      state.isError = false;
      state.userData = null;
      state.otpData = null;
      state.errorCode = null;
      state.errorMsg = null;
      state.clientToken = null;
    },
    resetErrMsg: (state) => {
      state.errorMsg = null;
    },
    resetLogout: (state, logoutState) => {
      // please send only the states that are regarding to resetting the state of log out
      // navigator('/');
      state.clientToken =
        logoutState?.payload?.clientToken === null
          ? logoutState?.payload?.clientToken
          : state.clientToken;
      state.errorMsg =
        logoutState?.payload?.errorMsg === null
          ? logoutState?.payload?.errorMsg
          : state.errorMsg;
      state.isError = logoutState?.payload?.isError || state.isError;
      state.errorCode = logoutState?.payload?.errorCode || state.errorCode;
      if (logoutState?.payload?.errorCode === 401) {
        localStorage.clear();
      }
      state.clientToken = null;
    },
    clearJwt: (state) => {
      state.clientToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      
    .addCase(login.pending, (state) => {
        state.isLoading.otpVerificationData = true;
        state.errorMsg = null;
        state.isSuccess = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading.otpVerificationData = false;
        state.isSuccess = true;
        state.isError = false;
        state.errorMsg = null;
        state.otpData = action.payload;
      })
      .addCase(login.rejected, (state, action: { payload: any }) => {
        state.isLoading.otpVerificationData = false;
        state.isError = true;
        state.otpData = action.payload;
        state.errorMsg = action.payload.errorMsg;
      })

    // LOGIN
      .addCase(loginWithOtp.pending, (state) => {
        state.isLoading.userData = true;
        state.errorMsg = null;
        state.isSuccess = false;
        state.clientToken = null;
      })
      .addCase(loginWithOtp.fulfilled, (state, action) => {
        state.isLoading.userData = false;
        state.isSuccess = true;
        state.isError = false;
        state.errorMsg = null;
        state.otpData = null;
        state.userData = action.payload;
        state.clientToken = action.payload?.accessToken;
      })
      .addCase(loginWithOtp.rejected, (state, action: { payload: any }) => {
        state.isLoading.userData = false;
        state.isError = true;
        state.userData = action.payload;
        state.errorMsg = action.payload.errorMsg;
        state.clientToken = null;
      })

      // SIGNUP
      .addCase(signUpNewUser.pending, (state) => {
        state.isLoading.signUpData = true;
        state.errorMsg = null;
        state.isSuccess = false;
      })
      .addCase(signUpNewUser.fulfilled, (state, action) => {
        addAfterLoginNotification("Admin Create successfully", "success");
        state.isLoading.signUpData = false;
        state.isSuccess = true;
        state.isError = false;
        state.errorMsg = null;
        state.signUpResponse = action.payload;
      })
      .addCase(signUpNewUser.rejected, (state, action: { payload: any }) => {
        addAfterLoginNotification(action.payload.errorMsg, "error");
        state.isLoading.signUpData = false;
        state.isError = true;
        state.signUpResponse = action.payload;
        state.errorMsg = action.payload.errorMsg;
      })

      // FORGOT PASSWORD
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading.forgotPasswordData = true;
        state.errorMsg = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading.forgotPasswordData = false;
        state.isSuccess = true;
        state.isForgotPasswordSuccess = true;
        state.errorMsg = null;
        state.forgotPasswordData = action.payload;
      })
      .addCase(forgotPassword.rejected, (state, action: { payload: any }) => {
        state.isLoading.forgotPasswordData = false;
        state.isError = true;
        state.userData = null;
        state.isForgotPasswordSuccess = false;
        state.forgotPasswordData = action.payload;
        state.errorMsg = action.payload.errorMsg;
      })

      // RESET PASSWORD
      .addCase(resetUserPassword.pending, (state) => {
        state.isLoading.resetPasswordData = true;
        state.errorMsg = null;
      })
      .addCase(resetUserPassword.fulfilled, (state, action) => {
        state.isLoading.resetPasswordData = false;
        state.isSuccess = true;
        state.errorMsg = null;
        state.isresetUserPasswordSuccess = true;
        state.resetPasswordData = action.payload;
      })
      .addCase(
        resetUserPassword.rejected,
        (state, action: { payload: any }) => {
          state.isLoading.resetPasswordData = false;
          state.isError = true;
          state.userData = null;
          state.isresetUserPasswordSuccess = false;
          state.resetPasswordData = null;
          state.errorMsg = action.payload.errorMsg;
        }
      )

      .addCase(userProfile.pending, (state) => {
        state.isLoading.profileData = true;
        state.errorMsg = null;
      })
      .addCase(userProfile.fulfilled, (state, action) => {
        state.isLoading.profileData = false;
        state.isSuccess = true;
        state.errorMsg = null;
        state.profileData = action.payload.data;
      })
      .addCase(userProfile.rejected, (state, action: { payload: any }) => {
        state.isLoading.profileData = false;
        state.isError = true;
        state.errorMsg = action.payload.errorMsg;
      })

      // LOGOUT
      .addCase(logout.pending, (state) => {
        state.isLoading.userData = true;
        state.errorMsg = null;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.isLoading.userData = false;
        state.isLoading.profileData = true;
        state.logoutData = action.payload;
        state.clientToken = null;
        state.userData = null;
        state.errorMsg = null;
      })
      .addCase(logout.rejected, (state, action: { payload: any }) => {
        state.isLoading.userData = false;
        state.isError = true;
        state.errorMsg = action.payload.errorMsg;
      });
  },
});

export const { reset, resetLogout, clearJwt, resetErrMsg, resetSignUp } =
  authSlice.actions;

export const selectedUser = (state: RootState) => {
  return state.rootReducer.auth;
};

export default authSlice.reducer;
